/* Tile Canvas and Tile (piece 4). The canvas is a grid whose row unit equals
   its column width, so a Tile's height in blocks is a height in column widths. */
.canvasWrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.canvasBar {
  display: flex;
  justify-content: flex-end;
}

.arrange {
  padding: var(--space-2);
  border: 1px dashed var(--border);
  border-radius: var(--radius-medium);
}

.canvas {
  display: grid;
  margin: 0;
  padding: 0;
  list-style: none;
}

.slot {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

/* A content-sized slot stands at its own height inside the rows it spans, so what is measured is the Tile and not the grid (decision 0321). */
.slot[data-sized='content'] {
  align-self: start;
}

/* A stretched slot carries a minimum height, and the Tile inside grows into it (decision 1080). */
.slot[data-sized='content'] > .tile {
  flex: 1 0 auto;
  max-height: var(--tile-max-height, none);
}

/* The canvas's own edit bar, outside the Tile: edit contexts only. It carries
   the padlock and, on an unlocked Tile, its width in columns, its height in
   blocks and a remove control. */
.editBar {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex: none;
  min-height: var(--button-height-small);
  margin-bottom: var(--space-1);
  padding: 0 var(--space-2);
  border-radius: var(--radius-small);
  background: var(--surface-2);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.padlock {
  display: inline-flex;
  align-items: center;
}

.editGroup {
  display: inline-flex;
  align-items: center;
  gap: var(--space-half);
}

.editButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--button-height-small);
  height: var(--button-height-small);
  padding: 0 var(--space-1);
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: var(--font-size-caption);
  cursor: pointer;
}

.editButton:hover:not(:disabled) {
  background: var(--surface);
  color: var(--text);
}

.editButton[aria-pressed='true'] {
  background: var(--navy-primary);
  color: var(--on-navy-fill);
}

.editButton:disabled {
  opacity: var(--control-disabled-opacity);
  cursor: not-allowed;
}

.editButton:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.editRemove {
  margin-left: auto;
}

.editValue {
  min-width: var(--space-4);
  text-align: center;
  color: var(--text);
}

/* The card the figures draw: white, a hairline edge, a soft lift off the ground. */
.tile {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  border: 1px solid var(--tile-border);
  border-radius: var(--radius-medium);
  background: var(--surface);
  box-shadow: var(--tile-shadow);
  overflow: hidden;
}

/* A tile that takes a drop says so while a file is over it. */
.tile[data-over='true'] {
  border-color: var(--navy-primary);
  box-shadow: inset 0 0 0 2px var(--navy-primary);
}

.header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex: none;
  min-height: var(--tile-header-height);
  padding: var(--space-2) var(--space-3) 0;
}

/* The title reads in small capitals, quiet, so the rows carry the weight (decision 0321); the navy dot beside it is gone (decision 1327). */
.header h3 {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  text-transform: uppercase;
}

/* A header with a second action never cuts its title short (decision 2851): the title keeps its width and the buttons drop to a row of their own, at the right, when the tile is too narrow for all three. */
.headerTwoActions {
  flex-wrap: wrap;
}

.headerTwoActions h3 {
  flex: 1 0 auto;
  overflow: visible;
}

.headerSecondAction {
  margin-left: auto;
}

/* A section inside a tile: the title in the header's quiet capitals and its actions at the right (decision 6461). */
.sectionHead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  min-height: var(--tile-header-height);
}

.sectionHeadTitle {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--section-label-color);
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  letter-spacing: var(--section-label-tracking);
  text-transform: uppercase;
}

/* 🔒 The count beside a tile's title, quieter than the title itself (decision 1217). */
.count {
  margin-left: var(--space-2);
  font-weight: var(--font-weight-regular);
}

/* 🔒 An empty tile is its title row alone (decision 6700): the title takes its own width, None yet reads
   muted on the same row, and the Add action keeps the row's right. The row closes with the inset it opens on. */
.tile[data-tile-slim='true'] > .header {
  padding-bottom: var(--space-2);
}

.tile[data-tile-slim='true'] > .header h3 {
  flex: 0 1 auto;
}

.headerEmpty {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  color: var(--text-muted);
  font-size: var(--font-size-small);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The header's action is a quiet link-shaped button, never a filled one: the page's primary is the banner's, and a fill on every card shouted (decision 0321). */
.headerAction {
  display: inline-flex;
  align-items: center;
  flex: none;
  height: var(--button-height-small);
  padding: 0 var(--space-2);
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--navy-primary);
  font: inherit;
  font-size: var(--font-size-button-small);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.headerAction:hover {
  background: var(--selection-bg);
}

.headerAction:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* A control of the page's own stands before the fold chevron and keeps its width (decision 8840). */
.headerControl {
  display: inline-flex;
  align-items: center;
  flex: none;
}

/* The fold chevron sits at the header's far right, after any action the tile
   carries, and is sized to the touch minimum so it is a target on a phone. */
.headerFold {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: var(--touch-target-min);
  height: var(--touch-target-min);
  margin-left: auto;
  padding: 0;
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.headerFold:hover {
  background: var(--selection-bg);
  color: var(--navy-primary);
}

.headerFold:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* A category's glyph where a tile stands for a category, the Settings Index (decision 0289); a section tile draws none (decision 1327). */
.dot {
  display: inline-flex;
  color: var(--navy-dot);
}

.body {
  flex: 1 1 auto;
  min-height: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
}

.item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-small);
}

/*
 * 🔨 A list of people, files or places flows into as many columns as the tile
 * is wide enough to hold, one token minimum per column, so a two-column
 * Contacts tile reads as two columns of people and a one-column one as a list
 * (piece 4). The body decides by its own width and never by the viewport.
 */
.body[data-flow='columns'] {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--tile-column-min-width), 1fr));
  align-content: start;
}

.body[data-flow='columns'] .item:last-child {
  padding-bottom: var(--space-2);
}

/*
 * 🔒 A column of people is never narrower than a row whose Call and Email hold
 * its right edge beside a readable name (decision 0692). A body narrower than one
 * such column draws one column at its own width rather than overflowing.
 */
.body[data-flow='columns'][data-flow-people='true'] {
  grid-template-columns: repeat(auto-fill, minmax(min(var(--tile-person-column-min-width), 100%), 1fr));
}

/*
 * 🔒 A Files tile lays its files out as cards, one column on a phone, two in a
 * middling tile and three in a wide one, by the tile's own width and never the
 * viewport's (decision 7080). A column is never narrower than the card token
 * and never more than a third of the body, so a wide tile stops at three.
 */
.body[data-flow='columns'][data-flow-files='true'] {
  grid-template-columns: repeat(auto-fill, minmax(min(max(var(--file-card-min-width), calc(100% / 3 - var(--space-1))), 100%), 1fr));
}

/* The Person item's avatar stands the height of its two lines and centres on them (figure 40). */
/*
 * 🔒 The Person row (decision 0692, amending 0623): the avatar, the name with its
 * pill and its pencil on the name's own line, the caption beneath, and the reach
 * on one line at the far right. 🔨 The reach never wraps under the text: the text
 * column shrinks and wraps, and the row grows taller. The one thing that takes a
 * line of its own is the list a reach's chevron opens.
 */
.personItem {
  --avatar-size: var(--tile-person-avatar-size);
  flex-wrap: wrap;
  align-items: center;
  row-gap: var(--space-2);
}

.personAvatar {
  display: inline-flex;
  flex: none;
  align-self: center;
}

.personText {
  flex: 1 1 0;
  min-width: 0;
}

/* The name, its pill and its pencil read as one line, the pencil right after the name (product owner, 2026-09-24). */
.personName {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
}

.personName .itemLabel {
  flex: 0 1 auto;
  overflow-wrap: anywhere;
}

.personLink {
  color: inherit;
  text-decoration: none;
}

.personLink:hover {
  text-decoration: underline;
}

.personReach {
  display: flex;
  flex: none;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

/* A reach with more than one line: the button and its chevron drawn as one control in two zones, as a split button is. */
.reachSplit {
  display: inline-flex;
  align-items: stretch;
}

.reachSplit > a {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

/* The chevron overlaps its button's outline by one stroke, so the joined pair draws one line between them, not two (decision 1329). */
.reachChevron {
  margin-left: calc(var(--field-border-width) * -1);
  padding: 0 var(--space-1);
  border: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  cursor: pointer;
}

.reachChevron:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* The lines a chevron opens, in the row's own flow beneath the buttons, so a tile's scroll box never clips them. */
.reachChoices {
  display: flex;
  flex: 1 0 100%;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.reachChoice {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-small);
  background: var(--surface);
  color: var(--text);
  text-decoration: none;
}

.reachChoice:hover {
  background: var(--surface-2);
}

.reachChoice:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.reachChoiceName {
  font-weight: var(--font-weight-semibold);
}

.reachChoiceValue {
  color: var(--text-muted);
  white-space: nowrap;
}

/*
 * 🔒 A contact laid as one row (decision 11980): the avatar, the name with its pencil and trash on the name's own
 * line, the title on the line under it and the detail lines under that at the same indent, and the acts in one
 * column at the right that every row of the tile shares, so Email alone stands where every other Email stands.
 * 🔨 The words give up width and end in an ellipsis; the acts never stand over them. Below the small breakpoint the
 * acts drop under the lines as one act line, the way a phone's row puts its acts under its words.
 */
.body[data-flow-person-rows='true'] {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: var(--space-2);
  align-content: start;
}

.personRow {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  column-gap: var(--space-2);
  row-gap: var(--space-1);
}

.personRow .personAvatar {
  grid-column: 1;
  grid-row: 1;
}

.personRow .personText {
  grid-column: 2;
  grid-row: 1;
  min-width: 0;
}

.personRow .personReach {
  grid-column: 2;
  grid-row: 2;
  justify-self: start;
  margin-left: 0;
}

.personRow .reachChoices {
  grid-column: 2 / -1;
  align-items: flex-start;
}

.personRow .personName {
  flex-wrap: nowrap;
}

.personRow .personName .itemLabel {
  min-width: 0;
  overflow: hidden;
  overflow-wrap: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personRow .rowEdit {
  flex: none;
}

/* 🔨 The pencil and the trash on a contact's name line are drawn at the touch minimum, 44 points (T3750). T3764: the name line grows to hold them, so the title line under it is never overlapped by the target. */
.personRow .personName .rowEdit {
  width: var(--touch-target-min);
  height: var(--touch-target-min);
}

/* One slot to a kind of act, so a Call stands where every other Call stands whether or not its row has an Email (T3750). */
.reachSlot {
  display: inline-flex;
}

.personTitle {
  display: block;
  overflow: hidden;
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personRow .itemDetail {
  display: block;
}

.personRow .detailPart {
  /* One line each for the email and the phone (T3750; the owner, 2026-11-01, Q7b). */
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

@media (--bp-sm) {
  .body[data-flow-person-rows='true'] {
    grid-template-columns: auto minmax(0, 1fr) auto auto;
  }

  /* The acts stand in the tile's two act columns, Call in the first and every other act in the second, so each kind aligns down the tile. */
  .personRow .personReach {
    display: contents;
  }

  .personRow .reachSlot {
    grid-row: 1;
    justify-self: end;
  }

  .personRow .reachSlot[data-reach-slot='call'] {
    grid-column: 3;
  }

  .personRow .reachSlot[data-reach-slot='other'] {
    grid-column: 4;
  }

  .personRow .reachChoices {
    align-items: flex-end;
  }
}

.item:last-child {
  padding-bottom: var(--space-3);
}

/* A row's body, whether or not it links anywhere: the avatar or glyph, then the stacked text. */
.rowLink {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  min-width: 0;
  flex: 1 1 auto;
  color: inherit;
  text-decoration: none;
}

a.rowLink:hover .itemLabel {
  text-decoration: underline;
}

.pairLink {
  color: var(--navy-primary);
  text-decoration: none;
}

.pairLink:hover {
  text-decoration: underline;
}

.rowPill {
  display: inline-flex;
  margin-left: var(--space-2);
  vertical-align: middle;
}

/* Status-only rows sit side by side as pills (figure 33). */
.statusBody {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
}

.statusItem {
  padding: 0;
}

/* The Note item: author and date on one line, the tag on the next, then the text (figure 35). */
.noteItem {
  flex-direction: column;
  gap: var(--space-1);
}

.noteHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2);
  width: 100%;
}

.noteTag {
  display: inline-flex;
}

/* The flag, the pill and the author on the left; the time and the pencil on the
   right. Both wrap rather than push each other out of the row (product owner,
   2026-09-13). */
.noteWho,
.noteWhen {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.noteWhen {
  flex: none;
  justify-content: flex-end;
}

/* 🔒 The File item draws through the one File row, FileRow.module.css (decision 3010). */

.rowButton {
  display: inline-flex;
  margin-left: var(--space-2);
}

.itemLabel {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: var(--font-weight-semibold);
}

.itemText {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: var(--space-half);
}

.itemDetail {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

/* 🔨 A detail part never breaks inside itself; the line breaks between parts. */
.detailPart {
  white-space: nowrap;
}

.pairs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2) var(--space-3);
  width: 100%;
  margin: 0;
}

.pair dt {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

/* The value, then whatever the row can do with it: the maps link, the mail link,
   the dialer link, the one action and the pencil. They sit on one line with a
   gap and wrap together when the cell runs out of width, rather than butting
   against the value (product owner, 2026-09-14). */
.pair dd {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  min-width: 0;
  /* 🔒 A paragraph typed into a multi-line field reads line for line here too (T87; decision 0685); a one-line value draws as before. */
  white-space: pre-line;
  overflow-wrap: anywhere;
  font-weight: var(--font-weight-semibold);
}

/* Inside a pair the button already carries the gap the cell sets. */
.pair dd .rowButton {
  margin-left: 0;
}

.issueFlag {
  display: inline-flex;
  color: var(--status-warning-text);
}

.note {
  white-space: pre-line;
}

/* Decision 7180: a rich note's markup sets its own lines, so the plain note's kept line breaks do not apply, and its blocks stand close as a note's lines do. */
.note .noteRich {
  display: inline;
  white-space: normal;
}

.note .noteRich :is(p, div, h1, h2, h3, h4, blockquote) {
  margin: 0;
}

.note .noteRich :is(ul, ol) {
  margin: 0;
  padding-left: var(--space-5);
}

.note .noteRich :is(h1, h2, h3, h4) {
  font-size: inherit;
  font-weight: var(--font-weight-semibold);
}

.note .noteRich blockquote {
  padding-left: var(--space-2);
  border-left: 1px solid var(--border);
}

/* T2904: the text and the replies glyph share one inline flow, the glyph last. */
.noteBlock {
  display: flow-root;
  width: 100%;
}

.note[data-note-clamped][data-note-inline='true'] {
  display: inline;
  overflow: visible;
}

/* A float after the text lands at the right of the last line where it fits, and on the line beneath where it does not. */
.noteBubbleSlot {
  float: right;
  margin-left: var(--space-2);
  white-space: nowrap;
}

/* The preview: three lines and an ellipsis until Show more (specification 2.9, figure 36). */
.note[data-note-clamped='true'] {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--note-max-lines);
  overflow: hidden;
}

.noteMore {
  display: inline-flex;
  align-items: center;
  gap: var(--space-half);
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--navy-primary);
  font: inherit;
  font-size: var(--font-size-caption);
  cursor: pointer;
}

/* 🔒 A note with no replies draws Show more alone on its foot line (decision 5493). */
.noteFoot {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.noteMore:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* The Full-width row: name, an optional button, and a value right-aligned, all on one line (figure 44). */
.fullWidth {
  align-items: center;
  flex-wrap: wrap;
  row-gap: var(--space-1);
  border-top: 1px solid var(--surface-2);
}

/*
 * 🔨 The row is two groups, the name and the trailing column, and the trailing
 * column drops to a line of its own before the name gives up any width
 * (product owner, 2026-09-14). 💰 The value and the pill held their width while
 * the name held none, so an RFQ row in a tile narrower than the Tile's declared
 * minimum read one word to a line over a sideways scrollbar.
 */
.rowMain {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex: 1 1 auto;
  /* The name keeps the width its own longest word needs; below that the row wraps instead. */
  min-width: min-content;
}

.rowSide {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex: 0 1 auto;
  min-width: min-content;
  margin-left: auto;
}

/* A word longer than the row breaks rather than pushing the row sideways; min-content sizing is unchanged by break-word. */
.fullWidth .itemLabel {
  overflow-wrap: break-word;
}

.fullWidth .linkRowValue {
  margin-left: auto;
  white-space: nowrap;
}

/*
 * 🔨 An arrival and a departure stack, one instant to a line, so a span never
 * breaks across the row's width (product owner, 2026-09-13). A row carrying
 * one instant draws one line in the same column.
 */
.rowWhen {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-half);
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
}

.fullWidth .linkRowValue + .rowWhen {
  margin-left: var(--space-3);
}

/*
 * 🔒 A document list is one grid for the whole tile (decision 0591): the date, the
 * kind, the pill, the name and the row's controls, in the order the owner asked
 * for — "Ordering Date, Type, Pill, Name" (2026-09-20). Every row contributes its
 * cells to shared tracks, so the columns line up down the list. 💰 The Sales tile
 * drew through the shared full-width row, which is a flex row per document, so
 * nothing lined up with anything above it and the date printed twice.
 */
.documentBody {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: start;
  column-gap: var(--space-2);
  row-gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
}

/* 🔨 Below the small breakpoint a row stacks: the date, the kind and the pill above, the name beneath at the
   tile's whole width, so a document number is never broken mid-number (T976). */
.documentRow {
  display: grid;
  grid-template-columns: max-content max-content max-content minmax(0, 1fr) max-content;
  column-gap: var(--space-2);
  row-gap: var(--space-half);
  padding: 0;
}

.documentText {
  grid-row: 2;
  grid-column: 1 / -1;
  overflow-wrap: anywhere;
}

.documentControls {
  grid-row: 1;
  grid-column: 5;
}

/* A document number is one word at every width: it takes its own width and never breaks inside. A sentence in the
   same cell wraps like any other text. */
.documentRow .documentNumber {
  white-space: nowrap;
}

@media (--bp-sm) {
  /* The rows contribute cells to one grid, so the columns line up down the whole list. */
  .documentBody {
    grid-template-columns: max-content max-content max-content minmax(min-content, 1fr) max-content;
  }

  .documentRow {
    display: contents;
  }

  .documentText,
  .documentControls {
    grid-row: auto;
    grid-column: auto;
  }
}

.documentDate {
  white-space: nowrap;
}

.documentKind {
  white-space: nowrap;
}

.documentPill,
.documentControls {
  display: inline-flex;
  align-items: center;
}

/* The pill stands in its own column, so it carries no leading margin of its own here. */
.documentPill .rowPill {
  margin-left: 0;
}

/*
 * The heading a grouped list draws once over the rows under it: a sub-skill list
 * says which skill it lists (product owner, 2026-09-20).
 */
.groupHeading {
  padding-bottom: 0;
  font-size: var(--section-label-size);
  font-weight: var(--section-label-weight);
  color: var(--section-label-color);
  text-transform: uppercase;
  letter-spacing: var(--section-label-tracking);
}

.timeline {
  margin: 0;
  padding: 0;
  list-style: none;
  /* One grid for the whole list from the small breakpoint up, every entry contributing its cells to shared
     tracks, so the date column is as wide as the widest date and the pills stand in one column at the right.
     💰 Each entry was its own grid, so the text began wherever that row's date ended (owner, 2026-09-19: "have
     set delimited widths for date, text and tags"). Below it an entry is its own two-line grid, the dot, the
     date and the pill above and the text beneath at the tile's whole width (T976). */
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: var(--space-2);
}

/* 🔒 History's five-entry window (T588; decision 1404): the list is held to the
   measured height of five entries and scrolls inside for the rest. */
.timelineWindow {
  overflow-y: auto;
}

.timelineEntry {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto auto;
  column-gap: var(--space-2);
  row-gap: var(--space-half);
  align-items: baseline;
}

.timelineWhen {
  grid-column: 2;
  grid-row: 1;
  white-space: nowrap;
}

.timelineDot {
  grid-column: 1;
  grid-row: 1;
}

.timelineText {
  grid-column: 1 / -1;
  grid-row: 2;
  overflow-wrap: anywhere;
}

.timelinePill {
  grid-column: 4;
  grid-row: 1;
  justify-self: end;
}

.timelineControls {
  grid-column: 5;
  grid-row: 1;
}

@media (--bp-sm) {
  .timeline {
    grid-template-columns: auto max-content minmax(var(--tile-text-column-min-width), 1fr) max-content max-content;
    column-gap: var(--space-2);
    align-items: baseline;
  }

  .timelineEntry {
    display: contents;
  }

  .timelineWhen,
  .timelineDot,
  .timelineText,
  .timelinePill,
  .timelineControls {
    grid-column: auto;
    grid-row: auto;
  }
}

.timelineDot {
  width: var(--list-row-status-dot-size);
  height: var(--list-row-status-dot-size);
  border-radius: var(--radius-stadium);
  background: var(--navy-dot);
}

.checklist {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

/* A checklist row is read, not edited: its box is fixed rather than disabled, so a done step reads as done and not as greyed out (decision 0321). */
.item .checklist label:has(input:disabled),
.item .checklist label:has(input:disabled) input:disabled {
  opacity: 1;
  cursor: default;
}

/* The tenth item type: a row that names a place and links to it. */
.linkRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  color: inherit;
  text-decoration: none;
}

.linkRow:hover .itemLabel {
  text-decoration: underline;
}

.linkRowValue {
  font-variant-numeric: var(--figures);
  font-weight: 700;
}

.linkRowValue[data-uncounted='true'] {
  color: var(--text-muted);
  font-weight: 400;
}

/* 🔒 The composer stands at the top of the list, under the heading, inset like a row (decision 0910); it never scrolls away with the rows beneath it. */
.composer {
  flex: none;
  padding: var(--space-2) var(--space-3) 0;
}

/* The custom slot fills the tile like the body does, scrolling inside its frame rather than growing past it.
   🔒 Decision 2156: it carries the item inset by default, so content never stands against the frame's edge. */
.custom {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-2) var(--space-3);
}

/* A child that draws edge to edge on purpose, such as rows ruled across the tile, says so with data-tile-custom-flush. */
.custom:has(> [data-tile-custom-flush]) {
  padding: 0;
}

/* An aside stands at the tile's right beside the items, the way an item page draws its picture (piece 17; decision 0269).
   Below the md breakpoint it stands above them instead, so a phone's fields are never squeezed beside a picture (decision 0297). */
.split {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  gap: var(--space-2);
}

.split > .body {
  flex: 1 1 auto;
  min-width: 0;
}

.aside {
  flex: none;
  display: flex;
  flex-direction: column;
  order: -1;
  padding: var(--space-2) var(--space-3) 0;
  overflow-y: auto;
}

@media (--bp-md) {
  .split {
    flex-direction: row;
    gap: var(--space-3);
  }

  .aside {
    order: 0;
    padding: var(--space-2) var(--space-3) var(--space-2) 0;
  }
}

/* A file row's own pencil: File details, beside the row that opens the file (decision 0273). */
/* 🔨 The pencil is an outlined control, not a bare glyph: drawn muted with no border beside a labelled End it read as decoration and went unfound (product owner, 2026-09-12; decision 0337). */
/* 🔒 A row's pencil and its × are the borderless icon button (decision 1327): no box until hovered, the muted ink, navy on hover. */
.rowEdit {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--button-height-small);
  height: var(--button-height-small);
  padding: 0;
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

/* 🔒 A certificate's fact card stands in its own row, the card carrying the box (T3082; decision 7440). */
.certificateItem {
  padding-block: var(--space-1);
}

/* A row that reads muted: the pill and the text fade, the controls stay (decision 0337). */
.mutedRow {
  color: var(--text-muted);
}

.mutedRow .itemLabel,
.mutedRow .linkRowValue {
  font-weight: 400;
}

.rowEdit:hover {
  background: var(--surface-2);
  color: var(--navy-primary);
}

.rowEdit:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* A row's reach is a link that keeps an outline, so it never reads as text; the pencil beside it went bare (decision 1327). */
a.rowEdit {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  text-decoration: none;
}

/* A Dated Role leads with the role, the organisation and the span read as one
   caption line beneath it, and the pill and the pencil hold the right edge
   (decision 0367). */
.datedRoleItem {
  align-items: center;
}

.datedRoleItem .rowPill {
  margin-left: auto;
}

/*
 * 🔒 The Employments tile as a timeline (T3097; decision 7480). One job per employment, newest first, down a thin
 * rule at the left with a dot on it, filled while the job runs and hollow once it has ended. The company leads in
 * bold and links to the company, its span beside it, and the titles held there sit beneath as small lines. The
 * rule runs from the first dot to the last and no further.
 */
.jobsItem {
  display: block;
  padding-block: 0;
}

.jobs {
  margin: 0;
  padding: 0;
  list-style: none;
}

.job {
  --job-dot-top: var(--space-3);
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
}

.jobRail {
  position: relative;
  flex: none;
  align-self: stretch;
  width: var(--job-dot-size);
}

.jobRail::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc((var(--job-dot-size) - var(--job-rail-width)) / 2);
  width: var(--job-rail-width);
  background: var(--tile-border);
}

.jobFirst .jobRail::before {
  top: calc(var(--job-dot-top) + var(--job-dot-size) / 2);
}

.jobLast .jobRail::before {
  bottom: auto;
  height: calc(var(--job-dot-top) + var(--job-dot-size) / 2);
}

.jobFirst.jobLast .jobRail::before {
  display: none;
}

.jobDot {
  position: absolute;
  top: var(--job-dot-top);
  left: 0;
  box-sizing: border-box;
  width: var(--job-dot-size);
  height: var(--job-dot-size);
  border: var(--job-rail-width) solid var(--navy-dot);
  border-radius: var(--radius-stadium);
  background: var(--surface);
}

.jobRunning .jobDot {
  background: var(--navy-dot);
}

.jobBody {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--space-half);
  min-width: 0;
  padding-block: var(--space-2);
}

.jobTop {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-half) var(--space-2);
}

.jobCompany {
  font-weight: var(--font-weight-semibold);
  color: var(--navy-primary);
  text-decoration: none;
}

a.jobCompany:hover {
  text-decoration: underline;
}

.jobSpan {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.jobTop .rowPill {
  margin-left: auto;
}

.jobTitles {
  display: flex;
  flex-direction: column;
  gap: var(--space-half);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--font-size-caption);
}

.jobTitleMuted {
  color: var(--text-muted);
}

.jobControls {
  display: flex;
  flex: none;
  align-items: center;
  align-self: center;
}


/* The pager under a long tile's rows: the count on the left, the controls after
   it, on one line that wraps at phone width rather than overflowing. */
.pager {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--tile-border);
}

.pagerCount {
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

/*
 * 🔒 The stacked contact row (decision 0623): the kind's glyph, the kind's name
 * over the job it is held in, the value, and the action. The row is three
 * groups, and at a phone's width the value and the action drop to a second line
 * rather than squeezing either the name or the number.
 * 🔨 The glyph is centred on the whole row, not on its first line: *"if placing
 * the icon of the type on the far left that is fine but verticaly center it to
 * the context"* (product owner, 2026-09-22).
 */
.contactRow {
  align-items: center;
  flex-wrap: wrap;
  row-gap: var(--space-1);
}

.contactIcon {
  display: inline-flex;
  align-self: center;
  flex: 0 0 auto;
  color: var(--text-muted);
}

.contactRow .itemText {
  flex: 1 1 auto;
  min-width: min-content;
}

/* The row's own word for the line and its primary mark, beside the kind's name and lighter than it. */
.contactQualifier {
  margin-left: var(--space-1);
  font-weight: var(--font-weight-regular);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

/* The middle column: the number as it is formatted, or the address as it was typed. */
.contactValue {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

/*
 * The action at the right, drawn as the small button's own shape so a Call reads
 * as something to press. 🔨 It is an anchor, so the device takes it.
 */
.contactAction {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: var(--button-height-small);
  padding: 0 calc(var(--button-height-small) * 0.5);
  border-radius: calc(var(--button-height-small) * var(--button-radius-ratio));
  background: var(--surface);
  box-shadow: inset 0 0 0 var(--field-border-width) var(--status-inactive-text);
  color: var(--status-inactive-text);
  font-size: var(--font-size-button-small);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
}

/* 🔒 A dialable line's Call in a row is outlined in green with the phone glyph (decision 1329, narrowing 0623): a list of people reads as names first, not as a column of green blocks. */
.contactCall {
  background: var(--surface);
  box-shadow: inset 0 0 0 var(--field-border-width) var(--status-good-text);
  color: var(--status-good-text);
}

/* 🔒 What a person row carries under its name outside a tile (decision 0719): the Deal's Authority row puts the role pill and the note there. */
.personBelow {
  display: grid;
  justify-items: start;
  gap: var(--space-1);
  padding-top: var(--space-1);
}

/* 🔒 The stacked person rows outside a tile (decision 0719): the tile's own rows, set flush with the card they stand in. */
.personList {
  display: grid;
  margin: 0;
  padding: 0;
  list-style: none;
}

.personList > .item {
  padding-left: 0;
  padding-right: 0;
}

/*
 * 🔒 A notes tile stands at its content's height, whatever blocks it declares
 * (decision 0950): its list holds itself to three notes and scrolls inside, so a
 * block's height never clips the list or leaves room for a fourth. 💰 It was one
 * block — six tenths of a column's width, heading and composer counted — at two
 * and three columns, and every note at one column.
 */
.slot[data-sized='content'] > .tile[data-note-list-tile='true'] {
  max-height: none;
}

/*
 * 🔒 A History tile stands at its content's height the same way (T588; decision
 * 1404): its list holds itself to five entries and scrolls inside, so a block's
 * height neither clips the window nor leaves a gap under it.
 */
.slot[data-sized='content'] > .tile[data-row-window-tile='true'] {
  max-height: none;
}

/*
 * 🔒 A document the viewer cannot open (decision 10370, building 10240): its words read as plain text, never as a link,
 * and the whole row is the press that shows the page's Permission denied banner.
 */
.refusedRow {
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: not-allowed;
}

.refusedText {
  color: var(--text);
}
